import { useState } from 'react'
import { ChevronDown, Download, Loader2 } from 'lucide-react'
import {
  reportBtnGhost, reportCard, reportCardPadSm, reportCardSub, reportEyebrow, reportPill,
} from '../reports/ui'
import { CountUp } from '../ui/primitives'

interface DimensionRollup {
  incidents: number
  resolved: number
  still_open: number
  vehicles: number
  median_hours: number | null
  p90_hours: number | null
  max_idle_hours: number | null
  avg_km_away: number | null
}

interface SnapshotData {
  from: string
  to: string
  near_hub_km: number
  totals: DimensionRollup
  by_category: Record<string, DimensionRollup>
  by_severity: Record<string, DimensionRollup>
  by_hub_proximity: Record<string, DimensionRollup>
}

const SEVERITY_TONE: Record<string, 'ok' | 'warn' | 'danger'> = {
  minor: 'ok', major: 'warn', immobilised: 'danger',
}

function fmtHours(h: number | null): string {
  if (h == null) return '—'
  return `${h}h`
}

function fmtKm(km: number | null): string {
  if (km == null) return '—'
  return `${km} km`
}

/** Today, formatted for a native date input's value/max. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function DimensionTable({
  title, rows, labelHeader,
}: {
  title: string
  rows: [string, DimensionRollup][]
  labelHeader: string
}): JSX.Element {
  return (
    <div className={`${reportCard} overflow-hidden`}>
      <div className="border-b border-slate-100 px-3.5 py-2.5">
        <h4 className="text-[11.5px] font-semibold text-slate-700">{title}</h4>
      </div>
      {rows.length === 0 ? (
        <p className="px-3.5 py-4 text-center text-[11px] text-slate-400">No incidents in this window.</p>
      ) : (
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              <th className="px-3.5 py-1.5 text-left">{labelHeader}</th>
              <th className="px-2 py-1.5 text-right">Incidents</th>
              <th className="px-2 py-1.5 text-right">Still down</th>
              <th className="px-2 py-1.5 text-right">Median TAT</th>
              <th className="px-2 py-1.5 text-right">Avg km from hub</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map(([label, r]) => (
              <tr key={label} className="transition-colors duration-100 hover:bg-slate-50/60">
                <td className="px-3.5 py-1.5 font-medium text-slate-700">
                  {SEVERITY_TONE[label] ? (
                    <span className={reportPill(SEVERITY_TONE[label])}>{label}</span>
                  ) : label}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-700">{r.incidents}</td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">
                  {r.still_open > 0 ? (
                    <span className="text-rose-600">
                      {r.still_open}
                      {r.max_idle_hours != null && (
                        <span className="block text-[9.5px] font-normal text-rose-400">
                          up to {fmtHours(r.max_idle_hours)} idling
                        </span>
                      )}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">{fmtHours(r.median_hours)}</td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">{fmtKm(r.avg_km_away)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Daily snapshot: today's breakdowns cut by severity, by how close to a hub it happened,
 * and by fault category, plus the overall reported-vs-resolved TAT picture -- collapsed by
 * default so it doesn't compete with "Currently held up" for attention, and downloadable as
 * the same workbook this panel's own numbers come from (so the two never disagree).
 */
export function BreakdownSnapshot(): JSX.Element {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(() => todayIso())
  const [data, setData] = useState<SnapshotData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = (forDate: string): void => {
    setLoading(true)
    setError(null)
    // `to` is only sent when the picker has moved off today: the no-`to` path means
    // "through right now" (live), which a same-day `to` would otherwise truncate to
    // midnight and silently exclude everything that happened since.
    const qs = forDate === todayIso() ? '' : `&to=${forDate}`
    fetch(`/api/breakdowns/snapshot?period=daily${qs}`)
      .then(async (r) => {
        const body = await r.json() as SnapshotData & { error?: string }
        if (!r.ok || body.error) throw new Error(body.error ?? String(r.status))
        setData(body)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }

  const toggle = (): void => {
    const next = !open
    setOpen(next)
    if (next && !data && !loading) load(date)
  }

  const onDateChange = (next: string): void => {
    setDate(next)
    setData(null)
    if (open) load(next)
  }

  const isToday = date === todayIso()
  const t = data?.totals

  return (
    <section className={`${reportCard} overflow-hidden`}>
      <button
        type="button" onClick={toggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-100 hover:bg-slate-50/60"
      >
        <div className="flex items-center gap-2">
          <ChevronDown size={14} className={`shrink-0 text-slate-400 transition-transform duration-150 ${open ? 'rotate-0' : '-rotate-90'}`} />
          <div>
            <div className={reportEyebrow}>Daily snapshot</div>
            <p className={reportCardSub}>
              {isToday ? "Today's" : `${date}'s`} breakdowns — severity, hub proximity, and TAT in one view.
            </p>
          </div>
        </div>
        {t && !loading && (
          <span className="hidden shrink-0 items-center gap-3 font-mono text-[11px] text-slate-500 sm:flex">
            <CountUp value={t.incidents} /> incidents
            {t.still_open > 0 && <span className="text-rose-600">{t.still_open} still down</span>}
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4">
          <div className="mb-3 flex items-center gap-2">
            <label className="text-[10.5px] font-medium uppercase tracking-wide text-slate-400" htmlFor="snapshot-date">
              Date
            </label>
            <input
              id="snapshot-date"
              type="date"
              value={date}
              max={todayIso()}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { if (e.target.value) onDateChange(e.target.value) }}
              className="rounded-md border border-slate-200 px-2 py-1 text-[11.5px] font-mono text-slate-700"
            />
            {!isToday && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDateChange(todayIso()) }}
                className="text-[11px] font-medium text-blue-600 hover:underline"
              >
                Jump to today
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-[12px] text-slate-400">
              <Loader2 size={13} className="animate-spin" /> Building {isToday ? "today's" : `${date}'s`} snapshot…
            </div>
          ) : error ? (
            <p className="py-4 text-center text-[12px] text-rose-600">Snapshot unavailable — {error}</p>
          ) : data && t ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {[
                  { label: 'Total incidents', value: t.incidents },
                  { label: 'Resolved', value: t.resolved },
                  { label: 'Still down', value: t.still_open, danger: t.still_open > 0 },
                  { label: 'Vehicles affected', value: t.vehicles },
                ].map((c) => (
                  <div key={c.label} className={`${reportCardPadSm} rounded-lg bg-slate-50`}>
                    <div className={`font-mono text-xl font-semibold tabular-nums ${c.danger ? 'text-rose-600' : 'text-slate-900'}`}>
                      <CountUp value={c.value} />
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-slate-500">{c.label}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-slate-500">
                <span>Median TAT: <span className="text-slate-800">{fmtHours(t.median_hours)}</span></span>
                <span>90th pct TAT: <span className="text-slate-800">{fmtHours(t.p90_hours)}</span></span>
                {t.max_idle_hours != null && (
                  <span>Longest idling now: <span className="text-rose-600">{fmtHours(t.max_idle_hours)}</span></span>
                )}
              </div>

              <div className="grid gap-3 lg:grid-cols-3">
                <DimensionTable title="By severity" labelHeader="Severity"
                  rows={Object.entries(data.by_severity)} />
                <DimensionTable title="By hub" labelHeader="Hub"
                  rows={Object.entries(data.by_hub_proximity)} />
                <DimensionTable title="By fault category" labelHeader="Category"
                  rows={Object.entries(data.by_category)} />
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <p className={reportCardSub}>
                  Full incident-level detail is in the downloadable workbook.
                </p>
                <a
                  href={`/api/reports/breakdown-report.xlsx?period=daily${isToday ? '' : `&to=${date}`}`}
                  className={reportBtnGhost}
                >
                  <Download size={12} /> Download {isToday ? "today's" : "this day's"} workbook
                </a>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </section>
  )
}
